import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "collaboratore" });

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "collaboratore" });
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "collaboratore",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Nome ed email sono obbligatori"); return; }
    if (!editing && !form.password) { toast.error("La password è obbligatoria per un nuovo utente"); return; }

    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success(form.password ? "Utente e password aggiornati" : "Utente aggiornato");
      } else {
        await api.post("/users", form);
        toast.success("Utente creato");
      }
      setOpen(false);
      resetForm();
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/users/${id}`); load(); };

  return (
    <div data-testid="users-page">
      <PageHeader title="Utenti" subtitle="Gestisci accessi e ruoli del team.">
        <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-user-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1" />Nuovo utente</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="user-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Email</Label><Input type="email" disabled={!!editing} data-testid="user-email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div>
                <Label>{editing ? "Nuova password" : "Password"}</Label>
                <Input type="password" data-testid="user-password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder={editing ? "Lascia vuoto per non modificarla" : ""} />
                {editing && <div className="text-[11px] text-slate-500 mt-1">Compila solo se vuoi cambiare la password.</div>}
              </div>
              <div><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                  <SelectTrigger data-testid="user-role" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="collaboratore">Collaboratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="user-save" className="bg-blue-600 hover:bg-blue-700">{editing ? "Salva modifiche" : "Crea"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <PageContent>
        <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500 font-mono">
              <tr><th className="text-left py-3 px-4">Nome</th><th className="text-left py-3 px-4">Email</th><th className="text-left py-3 px-4">Ruolo</th><th className="text-right py-3 px-4">Azioni</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-slate-800" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 text-white font-medium">{u.name}</td>
                  <td className="px-4 text-slate-400 font-mono text-xs">{u.email}</td>
                  <td className="px-4"><span className="text-[10px] px-2 py-0.5 rounded font-mono uppercase bg-blue-600/20 text-blue-300">{u.role}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>openEdit(u)} className="h-8"><Edit2 size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>remove(u.id)} className="text-red-400 h-8"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageContent>
    </div>
  );
}
